/**
 * Frontend analytics tracking hook
 *
 * Sends user activity events to the backend for tracking page views and actions.
 * Used to populate user_events table for determining user status (active/inactive/pending).
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '@/services/api'

interface TrackEventPayload {
  event_type: 'page_view' | 'action'
  page: string
  action?: string
  metadata?: Record<string, any>
}

/**
 * Send tracking event to backend (fire-and-forget, doesn't block UI)
 */
const trackEvent = async (payload: TrackEventPayload): Promise<void> => {
  try {
    // Fire and forget - don't await or handle errors
    api.post('/analytics/track', payload).catch(() => {
      // Silently ignore tracking errors
    })
  } catch {
    // Silently ignore tracking errors
  }
}

/**
 * Track page view when component mounts or route changes
 */
export const usePageTracking = () => {
  const location = useLocation()
  const previousPath = useRef<string>()

  useEffect(() => {
    // Only track if path actually changed (not just query params or hash)
    const currentPath = location.pathname
    if (currentPath !== previousPath.current) {
      trackEvent({
        event_type: 'page_view',
        page: currentPath,
        metadata: {
          search: location.search,
          hash: location.hash,
        },
      })
      previousPath.current = currentPath
    }
  }, [location])
}

/**
 * Track custom user actions
 */
export const useActionTracking = () => {
  const location = useLocation()

  return {
    trackAction: (action: string, metadata?: Record<string, any>) => {
      trackEvent({
        event_type: 'action',
        page: location.pathname,
        action,
        metadata,
      })
    },
  }
}
